import axios from "axios";
import {SpringPagedResult} from "@/service/PagedResult";
import {EmployeeStatus} from "@/enums";

const PATH = "/v3/employee-competencies";

/**
 * The competencies of an employee as the v3 API returns them. The employee's `id` is a uuid; the numeric employee
 * number is `employeeNumber`.
 */
export interface EmployeeCompetenciesV3Dto {
    employee: {
        id: string;
        employeeNumber: number;
        firstName: string;
        lastName: string;
    };
    companyLocation?: {
        id: string;
        name: string;
    } | null;
    quickCounter: boolean;
    allrounder: boolean;
    fruitAndVegetables: boolean;
    checkout: boolean;
    flowers: boolean;
    storage: boolean;
    storageManagement: boolean;
    weigh: boolean;
    reuse: boolean;
    check: boolean;
    targetActualComparison: boolean;
}

/**
 * The filters of {@link findEmployeeCompetencies}. `employeeNumber` matches employee numbers starting with its digits
 * and must be positive; `companyLocationId` is the uuid of a v2 company location.
 */
export interface EmployeeCompetenciesFilter {
    employeeNumber?: number | null;
    firstName?: string | null;
    lastName?: string | null;
    status?: EmployeeStatus | null;
    companyLocationId?: string | null;
    fruitAndVegetables?: boolean | null;
    checkout?: boolean | null;
    flowers?: boolean | null;
    storage?: boolean | null;
    storageManagement?: boolean | null;
    weigh?: boolean | null;
    reuse?: boolean | null;
    check?: boolean | null;
    targetActualComparison?: boolean | null;
    allrounder?: boolean | null;
    quickCounter?: boolean | null;
}

export default {
    /**
     * Returns one page of the competencies of the employees matching the filter, sorted by ascending employee number
     * unless `sort` says otherwise. `sort` accepts `employeeNumber`, `firstName`, `lastName`, `companyLocation` and
     * the name of each competency.
     */
    findEmployeeCompetencies (filter: EmployeeCompetenciesFilter | null, size: number | null, page: number | null, sort: string | null): Promise<SpringPagedResult<EmployeeCompetenciesV3Dto>> {
        return axios.get(PATH, {
            params: {
                employeeNumber: filter?.employeeNumber,
                firstName: filter?.firstName,
                lastName: filter?.lastName,
                status: filter?.status,
                companyLocationId: filter?.companyLocationId,
                allrounder: filter?.allrounder,
                quickCounter: filter?.quickCounter,
                fruitAndVegetables: filter?.fruitAndVegetables,
                checkout: filter?.checkout,
                flowers: filter?.flowers,
                storage: filter?.storage,
                storageManagement: filter?.storageManagement,
                weigh: filter?.weigh,
                reuse: filter?.reuse,
                check: filter?.check,
                targetActualComparison: filter?.targetActualComparison,
                size: size ? size : 250,
                page: page ? page : 0,
                sort: sort ? sort : null
            }
        }).then(response => response.data);
    },
};
